import { useEffect, useRef, useState } from "react";
import PrimaryButton from "../components/PrimaryButton";
import { BackLink, CardSubtitle, CardTitle, TextButton } from "../components/AuthCard";
import { AlertIcon, CheckIcon, ClockIcon } from "../components/Icons";
import OtpInput from "../components/OtpInput";
import { OTP_LENGTH } from "../constants";
import { formatTimer, useCountdown } from "../utils";

const EXPIRED_MESSAGE = "This OTP has expired. Request a new one.";

// Shared OTP entry screen — A3 (email), A5/A6 (mobile, first time), B3 (mobile, returning)
const OtpStep = ({
  header,
  title,
  description,
  config,
  showValidity = false,
  helperNote,
  submitLabel,
  error,
  errorCount = 0,
  onClearError,
  onSubmit,
  onResend,
  secondaryAction,
  backLabel = "Back",
  onBack,
}) => {
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [expiredError, setExpiredError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resent, setResent] = useState(false);
  const submitting = useRef(false);
  const [resendIn, restartResend] = useCountdown(config.resendSeconds);
  const [validFor, restartValidity] = useCountdown(config.validitySeconds);

  const visibleError = expiredError || error;
  const isComplete = otp.every(Boolean);

  const handleChange = (next) => {
    setOtp(next);
    if (expiredError) setExpiredError("");
    if (error) onClearError?.();
  };

  // "New code sent" confirmation fades after a few seconds
  useEffect(() => {
    if (!resent) return undefined;
    const id = setTimeout(() => setResent(false), 4000);
    return () => clearTimeout(id);
  }, [resent]);

  const handleSubmit = async () => {
    if (!isComplete || submitting.current) return;
    submitting.current = true;
    if (validFor <= 0) {
      setExpiredError(EXPIRED_MESSAGE);
      restartResend(0); // resend enabled immediately after expiry
      submitting.current = false;
      return;
    }
    setLoading(true);
    await onSubmit(otp.join(""));
    setLoading(false);
    submitting.current = false;
  };

  const handleResend = async () => {
    setResendLoading(true);
    const sent = await onResend();
    setResendLoading(false);
    if (sent === false) return;
    setOtp(Array(OTP_LENGTH).fill(""));
    setExpiredError("");
    setResent(true);
    onClearError?.();
    restartResend();
    restartValidity();
  };

  // Auto-verify as soon as the 6th digit lands (state is up to date on the next render)
  useEffect(() => {
    if (isComplete && !visibleError && !loading) handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          handleSubmit();
        }
      }}
    >
      {header}
      <CardTitle>{title}</CardTitle>
      <CardSubtitle>{description}</CardSubtitle>

      <div className="mt-6">
        <OtpInput
          key={errorCount}
          value={otp}
          onChange={handleChange}
          hasError={!!visibleError}
          disabled={loading}
        />
      </div>

      {visibleError && (
        <div className="flex items-center justify-center gap-1.5 mt-3 font-geomanistRegular text-xs leading-4 text-failed">
          <AlertIcon />
          {visibleError}
        </div>
      )}

      {resent && !visibleError && (
        <div className="flex items-center justify-center gap-1.5 mt-3 font-geomanistRegular text-xs leading-4 text-success">
          <CheckIcon />A new code has been sent
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mt-4 font-geomanistRegular text-xs leading-4 text-geomanistgray">
        <span className="flex items-center gap-1.5">
          <ClockIcon />
          <span>
            Didn&apos;t get it?{" "}
            <TextButton
              className="!text-xs"
              onClick={handleResend}
              disabled={resendIn > 0 || resendLoading}
            >
              Resend {showValidity ? "code" : "OTP"}
            </TextButton>
            {resendIn > 0 && (
              <>
                {" "}in{" "}
                <span className="font-geomanistBold text-blackShade">
                  {formatTimer(resendIn)}
                </span>
              </>
            )}
          </span>
        </span>
        {secondaryAction}
        {!secondaryAction && showValidity && (
          <span>Valid for {Math.round(config.validitySeconds / 60)} min</span>
        )}
      </div>

      {helperNote && (
        <div className="mt-2.5 rounded-lg bg-[#F7F7F8] px-4 py-2 font-geomanistRegular text-xs leading-4 text-geomanistgray">
          {helperNote}
        </div>
      )}

      <div className="mt-6">
        <PrimaryButton
          title={submitLabel}
          onClick={handleSubmit}
          loading={loading}
          disabled={!isComplete || loading}
        />
      </div>

      <BackLink label={backLabel} onClick={onBack} />
    </form>
  );
};

export default OtpStep;
